import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Editor, { type BeforeMount } from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';
import {
  algorithmsService,
  type Difficulty,
  type AlgoProblemDetail,
  type AlgoSubmission,
  type TestCaseResult,
  type SubmitResult,
  type RunResult,
} from '../services/algorithms.service';
import { useAuth } from '../contexts/AuthContext';
import { streamChat } from '../services/api';

// ─── Difficulty badge ──────────────────────────────────────────────────────────

function DifficultyBadge({ difficulty }: { difficulty: Difficulty }) {
  const styles: Record<Difficulty, string> = {
    EASY: 'bg-emerald-500/10 text-emerald-400',
    MEDIUM: 'bg-tertiary/10 text-tertiary',
    HARD: 'bg-error/10 text-error',
  };
  return (
    <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded ${styles[difficulty]}`}>
      {difficulty}
    </span>
  );
}

// ─── Test case result row ──────────────────────────────────────────────────────

function TestResultRow({ result, index }: { result: TestCaseResult; index: number }) {
  const [open, setOpen] = useState(!result.passed);
  return (
    <div className={`rounded-lg border ${result.passed ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-error/20 bg-error/5'}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2 text-sm"
      >
        <span className="flex items-center gap-2">
          <span className={`material-symbols-outlined text-base ${result.passed ? 'text-emerald-400' : 'text-error'}`}>
            {result.passed ? 'check_circle' : 'cancel'}
          </span>
          <span className="font-medium text-on-surface">Case {index + 1}</span>
          {result.runtime > 0 && (
            <span className="text-xs text-on-surface-variant">{result.runtime}ms</span>
          )}
        </span>
        <span className="material-symbols-outlined text-on-surface-variant text-sm">
          {open ? 'expand_less' : 'expand_more'}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-1.5 text-xs font-mono">
          {result.input !== '[hidden]' && (
            <div>
              <span className="text-tertiary">Input: </span>
              <span className="text-on-surface-variant">{result.input}</span>
            </div>
          )}
          <div>
            <span className="text-tertiary">Expected: </span>
            <span className="text-on-surface-variant">{result.expected}</span>
          </div>
          <div>
            <span className={result.passed ? 'text-emerald-400' : 'text-error'}>Output: </span>
            <span className="text-on-surface-variant">{result.actual}</span>
          </div>
          {result.error && (
            <div>
              <span className="text-error">Error: </span>
              <span className="text-on-surface-variant">{result.error}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Submission history row ────────────────────────────────────────────────────

function SubmissionRow({
  sub,
  onView,
}: {
  sub: AlgoSubmission;
  onView: (code: string) => void;
}) {
  const statusStyles: Record<string, string> = {
    ACCEPTED: 'text-emerald-400',
    WRONG_ANSWER: 'text-error',
    RUNTIME_ERROR: 'text-error',
    TIME_LIMIT_EXCEEDED: 'text-tertiary',
    PENDING: 'text-on-surface-variant',
  };

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-outline-variant/10 text-sm">
      <div className="flex items-center gap-3">
        <span className={`font-semibold ${statusStyles[sub.status] ?? ''}`}>
          {sub.status.replace(/_/g, ' ')}
        </span>
        {sub.runtime != null && (
          <span className="text-xs text-on-surface-variant">{sub.runtime}ms</span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-on-surface-variant">
          {new Date(sub.createdAt).toLocaleDateString()}
        </span>
        <button
          onClick={() => onView(sub.code)}
          className="text-xs text-primary hover:underline"
        >
          View
        </button>
      </div>
    </div>
  );
}

// ─── Console panel ─────────────────────────────────────────────────────────────

type ConsoleState =
  | { type: 'idle' }
  | { type: 'running' }
  | { type: 'run'; data: RunResult }
  | { type: 'submit'; data: SubmitResult };

function Console({ state }: { state: ConsoleState }) {
  if (state.type === 'idle') {
    return (
      <div className="text-xs text-on-surface-variant p-4">
        Run or submit to see results here.
      </div>
    );
  }

  if (state.type === 'running') {
    return (
      <div className="flex items-center gap-2 p-4">
        <div className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-xs text-on-surface-variant">Executing…</span>
      </div>
    );
  }

  const runResult = state.type === 'run' ? state.data : state.data.runResult;
  const isSubmit = state.type === 'submit';
  const allPassed = runResult.allPassed;

  return (
    <div className="p-4 space-y-3 overflow-y-auto max-h-full">
      {runResult.executionMode === 'heuristic' && (
        <div className="rounded-lg border border-amber-400/20 bg-amber-400/10 px-3 py-2 text-xs text-amber-300">
          This evaluator is heuristic for now. Use the result as practice feedback, not as a production-grade judge.
        </div>
      )}
      {isSubmit && (
        <div className={`flex items-center gap-2 text-sm font-bold ${allPassed ? 'text-emerald-400' : 'text-error'}`}>
          <span className="material-symbols-outlined text-base">
            {allPassed ? 'check_circle' : 'cancel'}
          </span>
          {allPassed ? 'Accepted' : 'Wrong Answer'}
          {allPassed && runResult.totalRuntime > 0 && (
            <span className="text-xs font-normal text-on-surface-variant ml-1">
              {runResult.totalRuntime}ms
            </span>
          )}
        </div>
      )}
      <div className="space-y-2">
        {runResult.results.map((r, i) => (
          <TestResultRow key={i} result={r} index={i} />
        ))}
      </div>
    </div>
  );
}

// ─── Monaco theme: suppress all word-highlight borders ─────────────────────────

const handleEditorWillMount: BeforeMount = (monaco) => {
  monaco.editor.defineTheme('algo-dark', {
    base: 'vs-dark',
    inherit: true,
    // Suppress any TextMate token that could appear as a visible whitespace glyph
    rules: [
      { token: 'white',               foreground: '00000000' },
      { token: 'whitespace',          foreground: '00000000' },
      { token: 'indent',              foreground: '00000000' },
      { token: 'meta.whitespace',     foreground: '00000000' },
    ],
    colors: {
      // Word / occurrence highlights → transparent
      'editor.wordHighlightBackground':       '#00000000',
      'editor.wordHighlightBorder':           '#00000000',
      'editor.wordHighlightStrongBackground': '#00000000',
      'editor.wordHighlightStrongBorder':     '#00000000',
      'editor.wordHighlightTextBackground':   '#00000000',
      'editor.wordHighlightTextBorder':       '#00000000',
      // Whitespace dots/markers → transparent (hides space glyphs in Python)
      'editorWhitespace.foreground':          '#00000000',
      // Indent guides → transparent
      'editorIndentGuide.background1':        '#00000000',
      'editorIndentGuide.activeBackground1':  '#00000000',
      'editorIndentGuide.background':         '#00000000',
      'editorIndentGuide.activeBackground':   '#00000000',
      // Bracket pair colorization → override all bracket colors to normal text color
      // so even if colorization runs, brackets look identical to surrounding text
      'editorBracketHighlight.foreground1':   '#d4d4d4',
      'editorBracketHighlight.foreground2':   '#d4d4d4',
      'editorBracketHighlight.foreground3':   '#d4d4d4',
      'editorBracketHighlight.foreground4':   '#d4d4d4',
      'editorBracketHighlight.foreground5':   '#d4d4d4',
      'editorBracketHighlight.foreground6':   '#d4d4d4',
      'editorBracketHighlight.unexpectedBracket.foreground': '#d4d4d4',
      // Bracket match highlight (when cursor is on a bracket) → transparent
      'editorBracketMatch.background':        '#00000000',
      'editorBracketMatch.border':            '#00000000',
      // Bracket pair guide boxes → transparent
      'editorBracketPairGuide.background1':        '#00000000',
      'editorBracketPairGuide.background2':        '#00000000',
      'editorBracketPairGuide.background3':        '#00000000',
      'editorBracketPairGuide.background4':        '#00000000',
      'editorBracketPairGuide.background5':        '#00000000',
      'editorBracketPairGuide.background6':        '#00000000',
      'editorBracketPairGuide.activeBackground1':  '#00000000',
      'editorBracketPairGuide.activeBackground2':  '#00000000',
      'editorBracketPairGuide.activeBackground3':  '#00000000',
      'editorBracketPairGuide.activeBackground4':  '#00000000',
      'editorBracketPairGuide.activeBackground5':  '#00000000',
      'editorBracketPairGuide.activeBackground6':  '#00000000',
    },
  });
};

// ─── Language config ────────────────────────────────────────────────────────────

type LangId = 'python' | 'javascript' | 'typescript' | 'java' | 'cpp' | 'go';

const LANGUAGES: { id: LangId; label: string; monacoLang: string; template: (slug: string) => string }[] = [
  {
    id: 'python',
    label: 'Python',
    monacoLang: 'python',
    template: () => 'class Solution:\n    pass\n',
  },
  {
    id: 'javascript',
    label: 'JavaScript',
    monacoLang: 'javascript',
    template: () => '/**\n * @param {number[]} nums\n * @return {number}\n */\nvar solve = function(nums) {\n\n};\n',
  },
  {
    id: 'typescript',
    label: 'TypeScript',
    monacoLang: 'typescript',
    template: () => 'function solve(nums: number[]): number {\n\n};\n',
  },
  {
    id: 'java',
    label: 'Java',
    monacoLang: 'java',
    template: () => 'class Solution {\n    public int solve(int[] nums) {\n        \n    }\n}\n',
  },
  {
    id: 'cpp',
    label: 'C++',
    monacoLang: 'cpp',
    template: () => 'class Solution {\npublic:\n    int solve(vector<int>& nums) {\n        \n    }\n};\n',
  },
  {
    id: 'go',
    label: 'Go',
    monacoLang: 'go',
    template: () => 'func solve(nums []int) int {\n\n}\n',
  },
];

// ─── Main page ─────────────────────────────────────────────────────────────────

type LeftTab = 'description' | 'solution' | 'submissions' | 'ai-tutor';

export default function AlgorithmProblem() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();

  const [leftTab, setLeftTab] = useState<LeftTab>('description');
  const [language, setLanguage] = useState<LangId>('python');
  const [langOpen, setLangOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'idle'>('idle');
  // Store code per language so switching back restores previous work
  const codeByLang = useRef<Partial<Record<LangId, string>>>({});
  const [code, setCode] = useState<string>('');
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [consoleState, setConsoleState] = useState<ConsoleState>({ type: 'idle' });

  // ── AI Tutor chat state ──────────────────────────────────────────────────────
  const [aiMessages, setAiMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [aiInput, setAiInput] = useState('');
  const [aiStreaming, setAiStreaming] = useState(false);
  const aiMessagesEndRef = useRef<HTMLDivElement>(null);

  const storageKey = useCallback(
    (lang: LangId) => `algo:${slug}:${lang}`,
    [slug],
  );

  // Autosave: persist to localStorage with 1s debounce
  const handleCodeChange = useCallback((value: string) => {
    setCode(value);
    codeByLang.current[language] = value;
    setSaveStatus('saving');
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(storageKey(language), value);
        setSaveStatus('saved');
      } catch {
        setSaveStatus('idle');
      }
    }, 1000);
  }, [language, storageKey]);

  // Cleanup timer on unmount
  useEffect(() => () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); }, []);

  const { data: problem, isLoading } = useQuery<AlgoProblemDetail>({
    queryKey: ['algo-problem', slug],
    queryFn: () => algorithmsService.getProblem(slug!),
    enabled: !!slug && !authLoading && !!user,
  });

  useEffect(() => {
    if (!problem) return;
    const saved = localStorage.getItem(storageKey('python'));
    const initial = saved ?? problem.starterCode;
    codeByLang.current.python = initial;
    setCode(initial);
    if (saved) setSaveStatus('saved');
  }, [problem, storageKey]);

  const { data: submissions = [] } = useQuery<AlgoSubmission[]>({
    queryKey: ['algo-submissions', problem?.id],
    queryFn: () => algorithmsService.getSubmissions(problem!.id),
    enabled: !!problem?.id && leftTab === 'submissions',
  });

  const handleLanguageChange = useCallback((lang: LangId) => {
    // Persist current code to localStorage before switching
    localStorage.setItem(storageKey(language), code);
    codeByLang.current[language] = code;
    setLanguage(lang);
    setLangOpen(false);
    // Restore saved code for the new language, or use template/starter
    const langConfig = LANGUAGES.find((l) => l.id === lang)!;
    const saved = localStorage.getItem(storageKey(lang));
    const next = saved ?? codeByLang.current[lang] ?? (lang === 'python' && problem?.starterCode ? problem.starterCode : langConfig.template(slug ?? ''));
    codeByLang.current[lang] = next;
    setCode(next);
    setSaveStatus(saved ? 'saved' : 'idle');
  }, [language, code, problem, slug, storageKey]);

  const handleResetCode = useCallback(() => {
    const langConfig = LANGUAGES.find((l) => l.id === language)!;
    const reset = language === 'python' && problem?.starterCode ? problem.starterCode : langConfig.template(slug ?? '');
    codeByLang.current[language] = reset;
    localStorage.setItem(storageKey(language), reset);
    setCode(reset);
    setSaveStatus('saved');
  }, [language, problem, slug, storageKey]);

  const runMutation = useMutation({
    mutationFn: () => algorithmsService.run(problem!.id, code, language),
    onMutate: () => {
      setConsoleOpen(true);
      setConsoleState({ type: 'running' });
    },
    onSuccess: (data) => setConsoleState({ type: 'run', data }),
    onError: () => setConsoleState({ type: 'idle' }),
  });

  const submitMutation = useMutation({
    mutationFn: () => algorithmsService.submit(problem!.id, code, language),
    onMutate: () => {
      setConsoleOpen(true);
      setConsoleState({ type: 'running' });
    },
    onSuccess: (data) => {
      setConsoleState({ type: 'submit', data });
      queryClient.invalidateQueries({ queryKey: ['algo-stats'] });
      queryClient.invalidateQueries({ queryKey: ['algo-problems'] });
      queryClient.invalidateQueries({ queryKey: ['algo-submissions', problem?.id] });
    },
    onError: () => setConsoleState({ type: 'idle' }),
  });

  const handleViewCode = useCallback((submittedCode: string) => {
    codeByLang.current[language] = submittedCode;
    localStorage.setItem(storageKey(language), submittedCode);
    setCode(submittedCode);
    setSaveStatus('saved');
    setEditorKey((k) => k + 1); // force Monaco remount so value prop is guaranteed to update
    setLeftTab('description');  // switch to description so editor is clearly visible
  }, [language, storageKey]);

  // ── AI Tutor ─────────────────────────────────────────────────────────────────

  const handleAiSend = useCallback(async (promptOverride?: string) => {
    const text = (promptOverride ?? aiInput).trim();
    if (!text || aiStreaming || !slug) return;

    const userMsg = { role: 'user' as const, content: text };
    const assistantMsg = { role: 'assistant' as const, content: '' };
    setAiMessages((prev) => [...prev, userMsg, assistantMsg]);
    setAiInput('');
    setAiStreaming(true);

    // Cap history to last 10 messages to keep token usage bounded
    const history = aiMessages.slice(-10).map((m) => ({ role: m.role, content: m.content }));
    const currentCode = code || problem?.starterCode || '';

    try {
      await streamChat(
        text,
        history,
        (chunk) => {
          setAiMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = { ...next[next.length - 1], content: next[next.length - 1].content + chunk };
            return next;
          });
        },
        () => setAiStreaming(false),
        (err) => {
          setAiMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = { ...next[next.length - 1], content: `Error: ${err}` };
            return next;
          });
          setAiStreaming(false);
        },
        undefined,
        undefined,
        undefined,
        'algo',
        undefined,
        slug,
        currentCode,
        language,
      );
    } catch {
      setAiMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = { ...next[next.length - 1], content: 'Network error. Please try again.' };
        return next;
      });
      setAiStreaming(false);
    }
  }, [aiInput, aiStreaming, slug, code, problem?.starterCode, aiMessages, language]);

  useEffect(() => {
    aiMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [aiMessages]);

  const isPending = runMutation.isPending || submitMutation.isPending;

  if (isLoading || !problem) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // Console height: open=220px, closed=40px
  const consoleH = consoleOpen ? 220 : 40;
  const editorH = `calc(100% - ${consoleH + 40}px)`; // 40px = editor toolbar

  return (
    // Full-viewport layout, no header padding — matches the design mockup
    <div className="flex flex-col lg:flex-row h-screen overflow-hidden">
      {/* ── Left pane: problem ──────────────────────────────────────── */}
      <section className="w-full lg:w-1/2 max-h-[50vh] lg:max-h-none flex flex-col border-b lg:border-b-0 lg:border-r border-outline-variant/10 bg-surface min-h-0">
        {/* Tab bar */}
        <div className="flex items-center px-4 h-10 bg-surface-container-low border-b border-outline-variant/10 shrink-0">
          <button
            onClick={() => navigate('/training/algorithms')}
            className="mr-4 text-on-surface-variant hover:text-on-surface transition-colors"
          >
            <span className="material-symbols-outlined text-lg">arrow_back</span>
          </button>
          {(['description', 'solution', 'submissions', 'ai-tutor'] as LeftTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setLeftTab(tab)}
              className={`h-full px-3 border-b-2 text-sm font-medium capitalize transition-colors ${
                leftTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface'
              }`}
            >
              {tab === 'ai-tutor' ? (
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">auto_awesome</span>
                  AI Tutor
                </span>
              ) : (
                tab
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className={`flex-1 p-8 min-h-0 ${leftTab === 'ai-tutor' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          {leftTab === 'description' && (
            <div className="space-y-6">
              {/* Title + badge */}
              <div className="flex items-start justify-between gap-4">
                <h1 className="text-2xl font-semibold tracking-tight text-on-surface leading-tight">
                  {problem.order}. {problem.title}
                </h1>
                <DifficultyBadge difficulty={problem.difficulty} />
              </div>

              {/* Topics */}
              <div className="flex flex-wrap gap-1.5">
                {problem.topics.map((t: string) => (
                  <span
                    key={t}
                    className="text-xs px-2.5 py-1 rounded bg-surface-container-high text-on-surface-variant"
                  >
                    {t}
                  </span>
                ))}
              </div>

              {/* Problem statement */}
              {problem.description && (
                <div className="prose-sm text-on-surface-variant space-y-3 leading-relaxed">
                  <ReactMarkdown
                    components={{
                      code: ({ children }) => (
                        <code className="bg-surface-container-high px-1 rounded text-primary text-xs font-mono">
                          {children}
                        </code>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc list-inside space-y-1 marker:text-primary">{children}</ul>
                      ),
                      strong: ({ children }) => (
                        <strong className="text-on-surface font-semibold">{children}</strong>
                      ),
                      p: ({ children }) => <p className="text-sm">{children}</p>,
                    }}
                  >
                    {problem.description}
                  </ReactMarkdown>
                </div>
              )}

              {/* Test cases preview */}
              {problem.testCases.filter((tc) => !tc.isHidden).length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-bold uppercase tracking-widest text-on-surface">Examples</p>
                  {problem.testCases
                    .filter((tc) => !tc.isHidden)
                    .map((tc, i: number) => (
                      <div
                        key={tc.id}
                        className="rounded-lg bg-surface-container-low border border-outline-variant/10 p-4 space-y-2 text-xs font-mono"
                      >
                        <p className="text-on-surface-variant font-sans font-semibold text-[11px] uppercase tracking-wider">
                          Example {i + 1}
                        </p>
                        <div>
                          <span className="text-on-surface-variant">Input: </span>
                          <span className="text-on-surface">{tc.input}</span>
                        </div>
                        <div>
                          <span className="text-on-surface-variant">Output: </span>
                          <span className="text-on-surface">{tc.expected}</span>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}

          {leftTab === 'solution' && (
            <div className="space-y-6">
              <h2 className="text-sm font-bold text-on-surface uppercase tracking-widest">Solution Walkthrough</h2>

              {/* YouTube video */}
              {problem.youtubeId ? (
                <div className="rounded-xl overflow-hidden aspect-video w-full">
                  <iframe
                    src={`https://www.youtube.com/embed/${problem.youtubeId}`}
                    title="Video explanation"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              ) : (
                <p className="text-sm text-on-surface-variant">No video available for this problem.</p>
              )}

              {/* Written guide */}
              {problem.examples && (
                <div className="prose-sm text-on-surface-variant space-y-4 leading-relaxed">
                  <ReactMarkdown
                    components={{
                      code: ({ children }) => (
                        <code className="bg-surface-container-high px-1 rounded text-primary text-xs font-mono">
                          {children}
                        </code>
                      ),
                      pre: ({ children }) => (
                        <pre className="bg-surface-container-lowest p-4 rounded-lg text-xs overflow-x-auto border border-outline-variant/10">
                          {children}
                        </pre>
                      ),
                      ul: ({ children }) => (
                        <ul className="list-disc list-inside space-y-1 marker:text-primary">{children}</ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="list-decimal list-inside space-y-1">{children}</ol>
                      ),
                      strong: ({ children }) => (
                        <strong className="text-on-surface font-semibold">{children}</strong>
                      ),
                      h1: ({ children }) => <h1 className="text-base font-bold text-on-surface mt-4">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-sm font-bold text-on-surface mt-3">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-sm font-semibold text-on-surface mt-2">{children}</h3>,
                      p: ({ children }) => <p className="text-sm">{children}</p>,
                    }}
                  >
                    {problem.examples}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          )}

          {leftTab === 'submissions' && (
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-widest text-on-surface mb-4">
                Your Submissions
              </p>
              {submissions.length === 0 ? (
                <p className="text-sm text-on-surface-variant">No submissions yet.</p>
              ) : (
                submissions.map((sub) => (
                  <SubmissionRow key={sub.id} sub={sub} onView={handleViewCode} />
                ))
              )}
            </div>
          )}

          {leftTab === 'ai-tutor' && (
            <div className="flex flex-col h-full">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
                {aiMessages.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                      <span className="material-symbols-outlined text-primary text-lg">auto_awesome</span>
                    </div>
                    <p className="text-sm font-medium text-on-surface mb-1">AI Tutor</p>
                    <p className="text-xs text-on-surface-variant max-w-xs mx-auto">
                      Ask for hints, explain the approach, or get help debugging your code. I'll know which problem you're working on.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2 mt-4">
                      {[
                        'Give me a hint',
                        'Explain the approach',
                        'Review my code',
                        'What is the time complexity?',
                      ].map((prompt) => (
                        <button
                          key={prompt}
                          onClick={() => handleAiSend(prompt)}
                          className="px-3 py-1.5 rounded-full bg-surface-container-high text-xs text-on-surface hover:bg-surface-container transition-colors"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {aiMessages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className="shrink-0">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center ${
                          msg.role === 'user'
                            ? 'bg-surface-container-highest'
                            : 'primary-gradient'
                        }`}
                      >
                        <span
                          className={`material-symbols-outlined text-xs ${
                            msg.role === 'user' ? 'text-on-surface-variant' : 'text-on-primary'
                          }`}
                          style={{ fontVariationSettings: "'FILL' 1" }}
                        >
                          {msg.role === 'user' ? 'person' : 'auto_awesome'}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`px-4 py-3 rounded-xl text-sm leading-relaxed max-w-[85%] ${
                        msg.role === 'user'
                          ? 'bg-surface-container-high text-on-surface rounded-tr-none'
                          : 'bg-surface-container text-on-surface rounded-tl-none border border-outline-variant/5'
                      }`}
                    >
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="text-sm mb-1 last:mb-0">{children}</p>,
                          strong: ({ children }) => <strong className="font-semibold text-on-surface">{children}</strong>,
                          ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-1">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 my-1">{children}</ol>,
                          li: ({ children }) => <li className="text-sm marker:text-primary">{children}</li>,
                          h1: ({ children }) => <h1 className="text-base font-bold text-on-surface mt-2 mb-1">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-sm font-bold text-on-surface mt-2 mb-1">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-sm font-semibold text-on-surface mt-1 mb-1">{children}</h3>,
                          code: ({ className, children }) => {
                            const isBlock = Boolean(className);
                            if (isBlock) {
                              return (
                                <pre className="bg-surface-container-lowest p-3 rounded-lg text-xs overflow-x-auto border border-outline-variant/10 my-2">
                                  <code>{children}</code>
                                </pre>
                              );
                            }
                            return (
                              <code className="bg-surface-container-high px-1 py-0.5 rounded text-primary text-xs font-mono">
                                {children}
                              </code>
                            );
                          },
                          pre: ({ children }) => <>{children}</>,
                        }}
                      >
                        {msg.content || (aiStreaming && i === aiMessages.length - 1 ? '...' : '')}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
                <div ref={aiMessagesEndRef} />
              </div>

              {/* Input */}
              <div className="mt-4 shrink-0">
                <div className="flex items-end gap-2 bg-surface-container-high rounded-xl px-3 py-2 border border-outline-variant/10">
                  <textarea
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAiSend();
                      }
                    }}
                    placeholder="Ask about this problem..."
                    rows={1}
                    disabled={aiStreaming}
                    className="flex-1 min-w-0 bg-transparent border-none focus:ring-0 text-sm text-on-surface placeholder:text-outline-variant resize-none outline-none leading-6 max-h-24 py-1"
                  />
                  <button
                    onClick={() => handleAiSend()}
                    disabled={!aiInput.trim() || aiStreaming}
                    className="w-9 h-9 shrink-0 rounded-xl primary-gradient text-on-primary flex items-center justify-center disabled:opacity-40 transition-opacity hover:opacity-90"
                  >
                    <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>
                      arrow_upward
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── Right pane: editor ──────────────────────────────────────── */}
      <section className="w-full lg:w-1/2 flex-1 flex flex-col bg-surface-container-lowest min-h-0">
        {/* Editor toolbar */}
        <div className="flex items-center justify-between px-4 h-10 bg-surface-container-low border-b border-outline-variant/10 shrink-0">
          {/* Language selector */}
          <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setLangOpen(false); }}>
            <button
              onClick={() => setLangOpen((o) => !o)}
              className="flex items-center gap-2 px-3 py-1 bg-surface-container-high rounded text-xs font-medium hover:bg-surface-container transition-colors"
            >
              <span className="text-primary">{LANGUAGES.find((l) => l.id === language)?.label}</span>
              <span className="material-symbols-outlined text-sm text-on-surface-variant">expand_more</span>
            </button>
            {langOpen && (
              <div className="absolute top-full left-0 mt-1 bg-surface-container-high rounded-lg shadow-lg border border-outline-variant/20 z-10 min-w-[130px] py-1">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.id}
                    onClick={() => handleLanguageChange(lang.id)}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-container transition-colors ${
                      language === lang.id ? 'text-primary font-semibold' : 'text-on-surface'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleResetCode}
              className="text-on-surface-variant hover:text-on-surface transition-colors"
              title="Reset to starter code"
            >
              <span className="material-symbols-outlined text-lg">refresh</span>
            </button>
          </div>
        </div>

        {/* Monaco Editor */}
        <div style={{ height: editorH }} className="overflow-hidden">
          <Editor
            key={editorKey}
            height="100%"
            language={LANGUAGES.find((l) => l.id === language)?.monacoLang ?? 'python'}
            value={code || problem.starterCode}
            onChange={(v) => handleCodeChange(v ?? '')}
            theme="algo-dark"
            beforeMount={handleEditorWillMount}
            options={{
              fontSize: 13,
              lineHeight: 24,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
              fontLigatures: true,
              padding: { top: 16, bottom: 16 },
              overviewRulerLanes: 0,
              hideCursorInOverviewRuler: true,
              scrollbar: { verticalScrollbarSize: 4, horizontalScrollbarSize: 4 },
              occurrencesHighlight: 'off',
              selectionHighlight: false,
              matchBrackets: 'never',
              bracketPairColorization: { enabled: false },
              snippetSuggestions: 'none',
              codeLens: false,
              renderWhitespace: 'none',
              renderValidationDecorations: 'off',
              guides: {
                indentation: false,
                highlightActiveIndentation: false,
                bracketPairs: false,
              },
            }}
          />
        </div>

        {/* Console */}
        <div
          className="border-t border-outline-variant/10 flex flex-col shrink-0 overflow-hidden"
          style={{ height: consoleH }}
        >
          {/* Console header */}
          <button
            onClick={() => setConsoleOpen((o) => !o)}
            className="flex items-center justify-between px-4 h-10 shrink-0 hover:bg-surface-container/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-on-surface">Console</span>
              {consoleState.type === 'submit' && (
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    consoleState.data.runResult.allPassed ? 'bg-emerald-400' : 'bg-error'
                  }`}
                />
              )}
              {consoleState.type === 'run' && (
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    consoleState.data.allPassed ? 'bg-emerald-400' : 'bg-error'
                  }`}
                />
              )}
            </div>
            <span className="material-symbols-outlined text-on-surface-variant text-sm">
              {consoleOpen ? 'keyboard_arrow_down' : 'keyboard_arrow_up'}
            </span>
          </button>

          {consoleOpen && (
            <div className="flex-1 overflow-y-auto min-h-0">
              <Console state={consoleState} />
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between px-4 h-14 bg-surface-container-low border-t border-outline-variant/10 shrink-0">
          <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-widest text-on-surface-variant">
            {saveStatus === 'saving' ? (
              <>
                <div className="w-1.5 h-1.5 rounded-full bg-tertiary animate-pulse" />
                Saving…
              </>
            ) : saveStatus === 'saved' ? (
              <>
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Saved
              </>
            ) : (
              <>
                <div className="w-1.5 h-1.5 rounded-full bg-outline-variant" />
                Auto-save
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => submitMutation.mutate()}
              disabled={isPending}
              className="px-5 py-2 rounded-md primary-gradient text-on-primary text-sm font-semibold hover:opacity-90 transition-all disabled:opacity-50 shadow-lg shadow-primary/10"
            >
              {submitMutation.isPending ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
